import { TABLE_COLUMNS_KEY, TABLE_META_KEY, TABLE_SESSION_PARAM_KEY } from '@/legacy/constants';
import { logger } from '@/shared/logger';

// --- Session storage: page, sort, filters, search (cleared on tab/reload) ---

export const readSessionParams = <T>(tableId: string): T | null => {
	try {
		const raw = sessionStorage.getItem(TABLE_SESSION_PARAM_KEY(tableId));
		return raw ? (JSON.parse(raw) as T) : null;
	} catch (error) {
		logger.error(`[TableStorage] Failed to read session params for table "${tableId}":`, error);
		return null;
	}
};

export const writeSessionParams = <T>(tableId: string, params: T): void => {
	try {
		sessionStorage.setItem(TABLE_SESSION_PARAM_KEY(tableId), JSON.stringify(params));
	} catch (error) {
		logger.error(`[TableStorage] Failed to write session params for table "${tableId}":`, error);
	}
};

export const removeSessionParams = (tableId: string): void => {
	sessionStorage.removeItem(TABLE_SESSION_PARAM_KEY(tableId));
};

export const clearAllTableSessionParams = (): void => {
	const prefix = TABLE_SESSION_PARAM_KEY('');
	Object.keys(sessionStorage)
		.filter((key) => key.startsWith(prefix))
		.forEach((key) => sessionStorage.removeItem(key));
};

// --- Local storage: column visibility only (survives reloads as a user preference) ---

export const readColumnsVisibility = (tableId: string): Record<string, boolean> | null => {
	try {
		const raw = localStorage.getItem(TABLE_COLUMNS_KEY(tableId));
		return raw ? (JSON.parse(raw) as Record<string, boolean>) : null;
	} catch (error) {
		logger.error(`[TableStorage] Failed to read column visibility for table "${tableId}":`, error);
		return null;
	}
};

export const writeColumnsVisibility = (
	tableId: string,
	visibility: Record<string, boolean>,
): void => {
	try {
		localStorage.setItem(TABLE_COLUMNS_KEY(tableId), JSON.stringify(visibility));
	} catch (error) {
		logger.error(`[TableStorage] Failed to write column visibility for table "${tableId}":`, error);
	}
};

// --- Local storage: version meta (used to detect breaking schema changes) ---

export const readMetaVersion = (tableId: string): string | null => {
	try {
		const raw = localStorage.getItem(TABLE_META_KEY(tableId));
		const obj = raw ? (JSON.parse(raw) as { version: string }) : null;
		return obj?.version ?? null;
	} catch (error) {
		logger.error(`[TableStorage] Failed to read meta version for table "${tableId}":`, error);
		return null;
	}
};

export const writeMetaVersion = (tableId: string, version: string): void => {
	try {
		localStorage.setItem(TABLE_META_KEY(tableId), JSON.stringify({ version }));
	} catch (error) {
		logger.error(`[TableStorage] Failed to write meta version for table "${tableId}":`, error);
	}
};
